import { Heading } from "@ui/components/Heading";
import { SkeletonBox } from "@ui/components/Skeleton";
import type { ReactNode } from "react";

export interface SearchResultsHeadingProps {
  /** Mobile sort menu etc.; the caller supplies its own responsive wrapper
   *  (`md:hidden` / `lg:hidden`) to match where its desktop control lives. */
  actions?: ReactNode;
  /** Pre-translated "N results" string — the seeded total on a full page
   *  load, the live total once the search settles. Null when neither is
   *  known yet (gated XHR navigations, degraded search), which skeletons. */
  heading: string | null;
  /**
   * "page" (default): the large page heading used in the desktop results
   * column. "sheet": the compact map-sheet strip — count centered at 16px
   * semibold (per the Figma peek state) with any actions trailing.
   */
  variant?: "page" | "sheet";
}

export function SearchResultsHeading({ actions, heading, variant = "page" }: SearchResultsHeadingProps) {
  if (variant === "sheet") {
    // The count stays optically centered in the sheet strip (per Figma); any
    // action rides the trailing edge without pushing the title off-center.
    // A <p>, not a heading: the page variant owns the document's single H1.
    return (
      <div className="relative flex min-h-9 items-center justify-center">
        <p className="text-center font-semibold text-base text-neutral-900">
          {heading ?? <SkeletonBox className="inline-block h-5 w-32 align-middle" />}
        </p>
        {actions ? <div className="absolute right-0 flex items-center">{actions}</div> : null}
      </div>
    );
  }

  return (
    <Heading.Root color="primary" size="lg">
      <Heading.Title as="h1">
        {heading ?? <SkeletonBox className="inline-block h-8 w-48 align-middle lg:h-11" />}
      </Heading.Title>
      {actions ? <Heading.Actions>{actions}</Heading.Actions> : null}
    </Heading.Root>
  );
}
